import { useCallback, useEffect, useState } from 'react';
import type { RecentItem } from '../types';

const STORAGE_KEY = 'command-palette-recents';
const MAX_ITEMS = 5;

function readFromStorage(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function useRecentItems() {
  const [items, setItems] = useState<RecentItem[]>(readFromStorage);

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setItems(readFromStorage());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const addRecent = useCallback((item: Omit<RecentItem, 'timestamp'>) => {
    setItems((current) => {
      const filtered = current.filter((r) => r.id !== item.id);
      const updated = [{ ...item, timestamp: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearRecents = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setItems([]);
  }, []);

  return { recentItems: items, addRecent, clearRecents };
}
